import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE } from "@/lib/constants";
import { Users, PaperPlaneTilt, Warning, LockKey, ArrowClockwise, DownloadSimple, QrCode, ShareNetwork, LinkSimple } from "@phosphor-icons/react";
import { StatCard, formatDate } from "./admin/shared";
import { KpiBand } from "./admin/KpiBand";
import { AnalyticsSection } from "./admin/AnalyticsSection";
import { ComparisonSection } from "./admin/ComparisonSection";
import { DailyBreakdown } from "./admin/DailyBreakdown";
import { CountryBreakdown } from "./admin/CountryBreakdown";
import { ReviewsSection } from "./admin/ReviewsSection";
import { FeedbackInbox } from "./admin/FeedbackInbox";
import { NotesSection } from "./admin/NotesSection";
import { WeeklyRecapCard } from "./admin/WeeklyRecapCard";
import { QrCodeSection } from "./admin/QrCodeSection";

const TOKEN_KEY = "stableish_admin_token";

// Subject-line options for the V1.1.1 announcement, keyed to match
// V1_1_1_SUBJECTS in email_service.py.
const V111_SUBJECT_LABELS = {
  descriptive: "What's new in Stable-ish",
  biggest: "Here's what's new in Stable-ish",
  toolkit: "A few fresh updates in Stable-ish",
  moments: "A quick look at what's new in Stable-ish",
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [tokenInput, setTokenInput] = useState("");
  const [authed, setAuthed] = useState(false);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [refreshingInstalls, setRefreshingInstalls] = useState(false);
  const [reviews, setReviews] = useState(null);
  const [comparison, setComparison] = useState(null);
  const [notes, setNotes] = useState([]);
  const [feedback, setFeedback] = useState({ unread: 0, items: [] });
  const [sendingWeekly, setSendingWeekly] = useState(false);
  const [daily, setDaily] = useState(null);
  const [dailyDays, setDailyDays] = useState(14);
  // V1.1.1 announcement state
  const [v111Stats, setV111Stats] = useState(null);
  const [v111Subject, setV111Subject] = useState("descriptive");
  const [v111Previewing, setV111Previewing] = useState(false);
  const [v111Sending, setV111Sending] = useState(false);
  const [sourceFilter, setSourceFilter] = useState("all");

  useEffect(() => {
    document.title = "Stable-ish · Admin";
  }, []);

  const fetchStats = useCallback(async (t) => {
    if (!t) return;
    setLoading(true);
    try {
      const headers = { "X-Admin-Token": t };
      const [statsRes, analyticsRes, reviewsRes, comparisonRes, notesRes, feedbackRes, dailyRes, v111Res] = await Promise.all([
        axios.get(`${API_BASE}/admin/stats`, { headers }),
        axios.get(`${API_BASE}/admin/analytics`, { headers }),
        axios.get(`${API_BASE}/admin/reviews`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_BASE}/admin/comparison`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_BASE}/admin/notes`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE}/admin/feedback`, { headers }).catch(() => ({ data: { unread: 0, items: [] } })),
        axios.get(`${API_BASE}/admin/daily-breakdown?days=${dailyDays}`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API_BASE}/admin/v1-1-1-announcement/stats`, { headers }).catch(() => ({ data: null })),
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setReviews(reviewsRes.data);
      setComparison(comparisonRes.data);
      setNotes(notesRes.data || []);
      setFeedback(feedbackRes.data || { unread: 0, items: [] });
      setDaily(dailyRes.data);
      setV111Stats(v111Res.data);
      setAuthed(true);
      sessionStorage.setItem(TOKEN_KEY, t);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 503) {
        toast.error("Invalid admin token.");
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        setAuthed(false);
      } else {
        toast.error("Couldn't reach the server. Try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [dailyDays]);

  useEffect(() => {
    if (token) fetchStats(token);
  }, [token, fetchStats]);

  const onLogin = (e) => {
    e.preventDefault();
    if (!tokenInput.trim()) return;
    setToken(tokenInput.trim());
    setTokenInput("");
  };

  const onLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAuthed(false);
    setStats(null);
    setAnalytics(null);
    setReviews(null);
    setComparison(null);
    setNotes([]);
    setFeedback({ unread: 0, items: [] });
    setDaily(null);
  };

  const onSendWeeklyPreview = async () => {
    setSendingWeekly(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/admin/weekly-recap/preview`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`Weekly recap queued to ${data.to}.`);
    } catch {
      toast.error("Couldn't send weekly recap preview.");
    } finally {
      setSendingWeekly(false);
    }
  };

  const onCreateNote = async (dateStr, text) => {
    try {
      const { data } = await axios.post(
        `${API_BASE}/admin/notes`,
        { date: dateStr, text },
        { headers: { "X-Admin-Token": token } }
      );
      setNotes((prev) => [data, ...prev]);
      toast.success("Note saved.");
      return true;
    } catch {
      toast.error("Couldn't save note.");
      return false;
    }
  };

  const onDeleteNote = async (id) => {
    if (!confirm("Delete this note?")) return;
    try {
      await axios.delete(`${API_BASE}/admin/notes/${id}`, {
        headers: { "X-Admin-Token": token },
      });
      setNotes((prev) => prev.filter((n) => n.id !== id));
      toast.success("Note deleted.");
    } catch {
      toast.error("Couldn't delete note.");
    }
  };

  const onMarkFeedbackRead = async (id) => {
    try {
      await axios.post(
        `${API_BASE}/admin/feedback/${id}/read`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      setFeedback((prev) => ({
        unread: Math.max(0, prev.unread - (prev.items.find((i) => i.id === id && !i.read) ? 1 : 0)),
        items: prev.items.map((i) => (i.id === id ? { ...i, read: true } : i)),
      }));
    } catch {
      toast.error("Couldn't mark feedback as read.");
    }
  };

  const onSendDigestPreview = async () => {
    try {
      const { data } = await axios.post(
        `${API_BASE}/admin/daily-digest/preview`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`Digest queued to ${data.to} · ${data.stats.yesterday_installs} installs yesterday.`);
    } catch {
      toast.error("Couldn't send digest preview.");
    }
  };

  // ── V1.1.1 update announcement ──
  const onSendV111Preview = async () => {
    if (!confirm(`Send a preview of the V1.1.5 email (subject: "${V111_SUBJECT_LABELS[v111Subject]}") to hello@stableish.me?`)) return;
    setV111Previewing(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/admin/v1-1-1-announcement/preview`,
        { subject_key: v111Subject },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`V1.1.5 preview queued to ${data.to}. Check your inbox.`);
    } catch {
      toast.error("Couldn't queue V1.1.5 preview.");
    } finally {
      setV111Previewing(false);
    }
  };

  const onSendV111All = async () => {
    const pending = v111Stats?.pending ?? 0;
    if (pending === 0) {
      toast.info("Nothing to send — everyone's already received the V1.1.5 email.");
      return;
    }
    const msg = `Send the V1.1.5 update email to ${pending} signup${pending === 1 ? "" : "s"} with subject "${V111_SUBJECT_LABELS[v111Subject]}"? This can't be undone.`;
    if (!confirm(msg)) return;

    setV111Sending(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/admin/v1-1-1-announcement/all`,
        { subject_key: v111Subject },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success(`V1.1.5 email queued for ${data.queued} signup${data.queued === 1 ? "" : "s"}.`);
      await fetchStats(token);
    } catch {
      toast.error("V1.1.5 send failed. Check backend logs.");
    } finally {
      setV111Sending(false);
    }
  };

  const onExportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API_BASE}/admin/waitlist.csv`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      // Extract filename from Content-Disposition, fall back to date-stamped default
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^"]+)"?/);
      const filename =
        match?.[1] ||
        `stableish-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;

      // Trigger browser download via blob URL
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${filename}`);
    } catch {
      toast.error("Export failed. Check backend logs.");
    } finally {
      setExporting(false);
    }
  };

  const onRefreshInstalls = async () => {
    setRefreshingInstalls(true);
    try {
      const { data } = await axios.post(
        `${API_BASE}/admin/refresh-installs?days=90`,
        {},
        { headers: { "X-Admin-Token": token } }
      );
      if (!data.configured) {
        toast.error("Apple credentials not configured on backend.");
      } else if (data.errors?.length) {
        toast.error(
          `Apple API error on ${data.errors.length} dates — check that your API key has "Sales" or "Admin" role.`
        );
      } else {
        toast.success(
          `Refreshed ${data.refreshed} day${data.refreshed === 1 ? "" : "s"} from Apple (${data.cached} already cached).`
        );
      }
      await fetchStats(token);
    } catch {
      toast.error("Refresh failed. Check backend logs.");
    } finally {
      setRefreshingInstalls(false);
    }
  };

  // Distinct sources present in current stats.recent. Must be declared
  // BEFORE the auth early-return so React hook order stays stable.
  // Normalize null/empty → "hero" to match the table's fallback.
  const availableSources = useMemo(() => {
    const set = new Set();
    for (const r of stats?.recent ?? []) set.add(r.source || "hero");
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [stats]);

  const filteredRecent = useMemo(() => {
    const recentList = stats?.recent ?? [];
    if (sourceFilter === "all") return recentList;
    return recentList.filter((r) => (r.source || "hero") === sourceFilter);
  }, [stats, sourceFilter]);

  // ---------- Login screen ----------
  if (!authed) {
    return (
      <main
        className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-5"
        data-testid="admin-login"
      >
        <form
          onSubmit={onLogin}
          className="w-full max-w-md border border-white/10 bg-[#0a0a0a] p-8"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 border border-[#FF2A2A]/40 bg-[#FF2A2A]/10 flex items-center justify-center text-[#FF2A2A]">
              <LockKey size={20} weight="duotone" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A]">
                // admin only
              </div>
              <h1 className="font-display text-2xl font-extrabold tracking-tight">
                Stable-ish console
              </h1>
            </div>
          </div>
          <p className="text-sm text-white/60 mb-5 leading-relaxed">
            Enter your admin token to open the dashboard.
          </p>
          <input
            type="password"
            required
            placeholder="admin token"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            data-testid="admin-token-input"
            className="w-full border border-white/15 bg-black px-4 py-3.5 text-white placeholder-white/30 font-mono text-sm focus:outline-none focus:border-[#FF2A2A]"
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-submit"
            className="mt-4 w-full bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-60 text-white px-4 py-3.5 font-mono text-xs uppercase tracking-[0.22em] transition-colors"
          >
            {loading ? "Verifying…" : "Enter"}
          </button>
          <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-white/35">
            token stored in this browser session only
          </p>
        </form>
      </main>
    );
  }

  // ---------- Dashboard ----------
  const total = stats?.total ?? 0;
  const recent = stats?.recent ?? [];

  return (
    <main
      className="min-h-screen bg-[#050505] text-white noise"
      data-testid="admin-dashboard"
    >
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-6 flex items-center justify-between">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A]">
              // admin console
            </div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">
              Stable<span className="text-[#FF2A2A]">-ish</span> broadcast
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onExportCsv}
              disabled={exporting}
              data-testid="admin-export-csv"
              className="border border-white/15 hover:border-white/40 text-white/85 hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors inline-flex items-center gap-2 disabled:opacity-50"
            >
              <DownloadSimple size={12} weight="bold" />
              {exporting ? "Exporting…" : "Export CSV"}
            </button>
            <button
              onClick={() => fetchStats(token)}
              data-testid="admin-refresh"
              className="border border-white/15 hover:border-white/40 text-white/70 hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors inline-flex items-center gap-2"
            >
              <ArrowClockwise size={12} weight="bold" />
              Refresh
            </button>
            <a
              href="/qr-card"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-qr-card-link"
              className="border border-white/15 hover:border-white/40 text-white/85 hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors inline-flex items-center gap-2"
            >
              <QrCode size={12} weight="bold" />
              QR Card
            </a>
            <a
              href="/qr-share"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-qr-share-link"
              className="border border-white/15 hover:border-white/40 text-white/85 hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors inline-flex items-center gap-2"
            >
              <ShareNetwork size={12} weight="bold" />
              QR Share
            </a>
            <a
              href="/go"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="admin-go-link"
              className="border border-white/15 hover:border-white/40 text-white/85 hover:text-white px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors inline-flex items-center gap-2"
            >
              <LinkSimple size={12} weight="bold" />
              Links Page
            </a>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="border border-white/15 hover:border-[#FF2A2A]/60 text-white/70 hover:text-[#FF2A2A] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.22em] transition-colors"
            >
              Log out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-10 space-y-10">
        {/* Weekly KPI band + source leaderboard — bird's-eye view */}
        <KpiBand comparison={comparison} />

        {/* Stats */}
        <section className="max-w-sm" data-testid="admin-stats">
          <StatCard
            icon={<Users size={20} weight="duotone" />}
            label="Total signups"
            value={total}
            testId="stat-total"
          />
        </section>

        {/* V1.1.1 update announcement */}
        <section
          className="border border-white/10 bg-[#0a0a0a] p-8"
          data-testid="admin-v111-broadcast-block"
        >
          <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#FF2A2A] mb-2">
            // v1.1.5 update email
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.05]">
            Announce the v1.1.5 update
            <br />
            <span className="italic font-light text-white/70">
              Personalized setup, smart nudges &amp; My Protocols.
            </span>
          </h2>
          <p className="mt-4 text-sm text-white/60 max-w-xl leading-relaxed">
            Sends the &ldquo;what&rsquo;s new&rdquo; email to
            {" "}{v111Stats?.pending ?? 0} signup{v111Stats?.pending === 1 ? "" : "s"} who haven&rsquo;t received it yet.
            Idempotent — anyone already emailed will be skipped.
          </p>

          {/* Subject line picker */}
          <div className="mt-6">
            <label
              htmlFor="v111-subject"
              className="block font-mono text-[10px] uppercase tracking-[0.22em] text-white/50 mb-2"
            >
              subject line
            </label>
            <select
              id="v111-subject"
              data-testid="admin-v111-subject"
              value={v111Subject}
              onChange={(e) => setV111Subject(e.target.value)}
              className="w-full max-w-xl bg-black border border-white/20 hover:border-white/40 text-white/90 px-4 py-3 text-sm focus:outline-none focus:border-[#FF2A2A] transition-colors"
            >
              {Object.entries(V111_SUBJECT_LABELS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Stats mini-row */}
          <div className="mt-5 flex flex-wrap gap-6 font-mono text-[10px] uppercase tracking-[0.22em] text-white/50">
            <span>
              total signups:{" "}
              <span className="text-white/90">{v111Stats?.total ?? "—"}</span>
            </span>
            <span>
              already sent:{" "}
              <span className="text-white/90">{v111Stats?.v1_1_1_sent ?? "—"}</span>
            </span>
            <span>
              pending:{" "}
              <span className={(v111Stats?.pending ?? 0) > 0 ? "text-[#FF2A2A]" : "text-white/40"}>
                {v111Stats?.pending ?? "—"}
              </span>
            </span>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <button
              onClick={onSendV111All}
              disabled={v111Sending || (v111Stats?.pending ?? 0) === 0}
              data-testid="admin-v111-send-all"
              className="group relative inline-flex items-center gap-3 bg-[#FF2A2A] hover:bg-[#FF4D4D] disabled:opacity-40 disabled:cursor-not-allowed text-white px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] transition-colors"
            >
              {/* Pulsing "ready to send" indicator — visible only when
                  there are pending signups waiting for the broadcast. */}
              {(v111Stats?.pending ?? 0) > 0 && !v111Sending ? (
                <span
                  className="absolute -top-1.5 -right-1.5 flex h-3 w-3"
                  data-testid="admin-v111-pending-pulse"
                  aria-label="Pending signups ready to receive"
                >
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-white ring-2 ring-[#FF2A2A]" />
                </span>
              ) : null}
              <PaperPlaneTilt size={16} weight="bold" />
              {v111Sending
                ? "Queuing…"
                : (v111Stats?.pending ?? 0) === 0
                  ? "Nothing to send"
                  : `Send to ${v111Stats.pending} signup${v111Stats.pending === 1 ? "" : "s"}`}
            </button>
            <button
              onClick={onSendV111Preview}
              disabled={v111Previewing}
              data-testid="admin-v111-send-preview"
              className="inline-flex items-center gap-3 border border-white/20 hover:border-white/50 text-white/85 px-6 py-4 font-mono text-xs uppercase tracking-[0.22em] transition-colors"
            >
              {v111Previewing ? "Sending preview…" : "Send preview to owner"}
            </button>
          </div>

          <div className="mt-5 flex items-start gap-2 text-[#FF2A2A]/90">
            <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
            <p className="font-mono text-[10px] uppercase tracking-[0.22em] leading-relaxed">
              Real emails go out immediately. Preview first to check the subject &amp; body.
            </p>
          </div>
        </section>

        {/* Analytics section */}
        <AnalyticsSection
          data={analytics}
          token={token}
          onRefreshInstalls={onRefreshInstalls}
          refreshingInstalls={refreshingInstalls}
          onSendDigestPreview={onSendDigestPreview}
        />

        {/* Period comparison — this week vs last week */}
        <ComparisonSection comparison={comparison} />

        {/* Day-by-day breakdown */}
        <DailyBreakdown
          data={daily}
          days={dailyDays}
          onDaysChange={(d) => setDailyDays(d)}
        />

        {/* Country / region breakdown from Apple installs */}
        <CountryBreakdown byCountry={analytics?.installs?.by_country ?? []} />

        {/* App Store reviews (Apple public RSS) */}
        <ReviewsSection reviews={reviews} />

        {/* Feedback inbox */}
        <FeedbackInbox feedback={feedback} onMarkRead={onMarkFeedbackRead} />

        {/* Launch journal / notes */}
        <NotesSection notes={notes} onCreate={onCreateNote} onDelete={onDeleteNote} />

        {/* Weekly recap email preview */}
        <WeeklyRecapCard onSend={onSendWeeklyPreview} sending={sendingWeekly} />

        {/* QR code generator */}
        <QrCodeSection />

        {/* Recent signups table */}
        <section data-testid="admin-recent">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-display text-xl font-extrabold tracking-tight">
              Recent signups
            </h3>
            <div className="flex items-center gap-3">
              <label
                htmlFor="admin-source-filter"
                className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45"
              >
                filter source
              </label>
              <select
                id="admin-source-filter"
                value={sourceFilter}
                onChange={(e) => setSourceFilter(e.target.value)}
                data-testid="admin-source-filter"
                className="bg-black border border-white/15 hover:border-white/40 focus:border-[#FF2A2A] text-white/85 font-mono text-[11px] uppercase tracking-[0.18em] px-3 py-2 focus:outline-none transition-colors"
              >
                <option value="all">all sources ({recent.length})</option>
                {availableSources.map((s) => {
                  const count = recent.filter(
                    (r) => (r.source || "hero") === s
                  ).length;
                  return (
                    <option key={s} value={s}>
                      {s} ({count})
                    </option>
                  );
                })}
              </select>
              <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 hidden md:inline">
                {filteredRecent.length === recent.length
                  ? `latest ${recent.length}`
                  : `${filteredRecent.length} of ${recent.length}`}{" "}
                · newest first
              </span>
            </div>
          </div>
          <div className="border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] border-b border-white/10">
                <tr className="text-left font-mono text-[10px] uppercase tracking-[0.22em] text-white/45">
                  <th className="py-3 px-4">Email</th>
                  <th className="py-3 px-4 hidden sm:table-cell">Role</th>
                  <th className="py-3 px-4 hidden md:table-cell">Source</th>
                  <th className="py-3 px-4 hidden lg:table-cell">Joined</th>
                </tr>
              </thead>
              <tbody>
                {filteredRecent.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="py-8 px-4 text-center font-mono text-[11px] uppercase tracking-[0.22em] text-white/35"
                    >
                      {recent.length === 0
                        ? "No signups yet"
                        : `No signups from source "${sourceFilter}"`}
                    </td>
                  </tr>
                )}
                {filteredRecent.map((r) => (
                  <tr
                    key={r.email}
                    className="border-t border-white/5 hover:bg-white/[0.02] transition-colors"
                    data-testid={`admin-row-${r.email}`}
                  >
                    <td className="py-3 px-4 font-mono text-xs text-white/85 break-all">
                      {r.email}
                    </td>
                    <td className="py-3 px-4 text-white/60 hidden sm:table-cell">
                      {r.role || <span className="text-white/25">—</span>}
                    </td>
                    <td className="py-3 px-4 font-mono text-[10px] uppercase tracking-[0.18em] text-white/50 hidden md:table-cell">
                      {r.source || "hero"}
                    </td>
                    <td className="py-3 px-4 font-mono text-[10px] text-white/50 hidden lg:table-cell">
                      {formatDate(r.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </main>
  );
}
